import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  CheckCircle,
  Calendar,
  Clock,
  Bell,
  MessageSquare,
  CreditCard,
  ArrowRight
} from "lucide-react";

const CheckinConfirmed = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    fetchConfirmation();
  }, [requestId]);

  const fetchConfirmation = async () => {
    try {
      const response = await axios.get(`${API}/checkin/${requestId}/status`);
      setConfirmation(response.data);
    } catch (error) {
      console.error("Error fetching confirmation:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <main className="max-w-lg mx-auto px-4 py-12">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Preferences Shared!</h1>
          <p className="text-gray-600">
            {confirmation?.stylist_name || "Your stylist"} will review and confirm your appointment.
          </p>
        </div>

        {/* What's Next */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold">What happens next?</h2>
          </div>
          <div className="divide-y divide-gray-100">
            <div className="flex items-start gap-4 p-4">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Style Confirmation</p>
                <p className="text-xs text-gray-600">Your stylist will message you to confirm your look and price</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Day-Before Reminder</p>
                <p className="text-xs text-gray-600">You'll get a text reminder the day before your appointment</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Easy Payment</p>
                <p className="text-xs text-gray-600">Pay your stylist directly via their preferred method after service</p>
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Details */}
        {confirmation?.appointment && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="font-medium text-sm mb-3">Your Request</h3>
            <div className="space-y-2 text-sm">
              {confirmation.appointment.service && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Service</span>
                  <span className="font-medium">{confirmation.appointment.service}</span>
                </div>
              )}
              {confirmation.appointment.preferred_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Preferred Date</span>
                  <span className="font-medium">{confirmation.appointment.preferred_date}</span>
                </div>
              )}
              {confirmation.appointment.preferred_time && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Preferred Time</span>
                  <span className="font-medium">{confirmation.appointment.preferred_time}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-gray-600">Status</span>
                <Badge variant="outline">Awaiting Confirmation</Badge>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="space-y-3">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="w-full bg-black hover:bg-gray-800"
          >
            Save Your Preferences
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-gray-500 text-center">
            Create a free account to save your preferences for future appointments
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-500 text-xs">
            <PhygitalIcon className="w-4 h-4" showBox={false} />
            <span>Powered by phygital</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckinConfirmed;
